/**
 * Low-level audio playback primitive. The cue dispatcher
 * (`sound.ts`) layers on top of this — it maps semantic cue names
 * to URLs + reads the persisted mute / volume preferences. This
 * module owns how a cue reaches the speaker, the autoplay policy it
 * has to satisfy, and the missing-asset failure modes. The tests
 * inject a backend via `setAudioPlayBackend`.
 */

export interface AudioPlayOptions {
  /** Override the playback volume (0..1). Values outside the range
   *  clamp; non-finite inputs fall through to the default. */
  readonly volume?: number;
  /** When `true`, the play call no-ops outright. Caller usually
   *  checks the mute store before invoking, but the parameter is
   *  here so a downstream gate can short-circuit without a branch. */
  readonly muted?: boolean;
}

/** Default playback volume when the caller doesn't pass one + the
 *  store doesn't carry an override. Tuned conservatively so a
 *  stack of overlapping cues never bursts on first load. */
export const DEFAULT_AUDIO_VOLUME = 0.55;

/** Clamp a candidate volume to the legal [0, 1] range. Pure
 *  function — exported so the volume slider component can apply the
 *  same gate before writing to the store. */
export function clampVolume(v: unknown): number {
  if (typeof v !== "number" || !Number.isFinite(v)) {
    return DEFAULT_AUDIO_VOLUME;
  }
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

/**
 * Pluggable backend so the unit tests can count invocations + read
 * the resolved volume without touching the browser's audio.
 */
export interface AudioPlayBackend {
  play(url: string, volume: number): void;
}

/**
 * One Web Audio context for the whole app, the cues decoded once.
 *
 * Every cue fires from a WebSocket frame, never from a tap. A fresh
 * `new Audio(url).play()` per cue is what a phone refuses when nothing
 * was touched a moment ago — iOS in particular lets a new element play
 * only just after a gesture — so a cue that followed the player's own
 * tap sounded and one that followed somebody else's action did not:
 * sound "sometimes". A desktop keeps the page's permission once
 * granted, which is why it never showed there.
 *
 * A context, once started inside a gesture, stays allowed. So the first
 * touch anywhere starts it (`installAudioUnlock`), and every later touch
 * starts it again if the phone suspended it — a lock screen, a call,
 * another app. The cues are fetched and decoded once rather than per
 * play.
 *
 * iOS: the context follows the ringer switch, as games do, and the
 * session is `ambient` so a cue mixes with the player's music instead
 * of stopping it.
 */
type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

let context: AudioContext | null = null;
const decoded = new Map<string, Promise<AudioBuffer | null>>();

function ensureContext(): AudioContext | null {
  if (context !== null) return context;
  const Ctor = audioContextCtor();
  if (Ctor === null) return null;
  const session = (navigator as unknown as { audioSession?: { type: string } })
    .audioSession;
  if (session !== undefined) session.type = "ambient";
  context = new Ctor();
  return context;
}

function bufferFor(
  ctx: AudioContext,
  url: string,
): Promise<AudioBuffer | null> {
  let pending = decoded.get(url);
  if (pending === undefined) {
    pending = fetch(url)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(url))))
      .then((bytes) => ctx.decodeAudioData(bytes))
      .catch(() => {
        // A missing or undecodable file is asked for again next time
        // rather than remembered as silence for the whole session.
        decoded.delete(url);
        return null;
      });
    decoded.set(url, pending);
  }
  return pending;
}

/** A cue this late is no longer about what is on screen. */
const STALE_AFTER_MS = 800;

const webAudioBackend: AudioPlayBackend = {
  play(url, volume) {
    const ctx = ensureContext();
    if (ctx === null) return;
    const asked = performance.now();
    void bufferFor(ctx, url).then((buffer) => {
      if (buffer === null || ctx.state !== "running") return;
      if (performance.now() - asked > STALE_AFTER_MS) return;
      const gain = ctx.createGain();
      gain.gain.value = volume;
      gain.connect(ctx.destination);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(gain);
      source.start();
    });
  },
};

/** Where Web Audio is missing, the element per cue — what there was. */
const elementBackend: AudioPlayBackend = {
  play(url, volume) {
    try {
      if (typeof Audio === "undefined") return;
      const audio = new Audio(url);
      audio.volume = volume;
      const result = audio.play();
      if (result && typeof result.catch === "function") {
        result.catch(() => undefined);
      }
    } catch {
      // `new Audio()` itself threw — silently skip.
    }
  },
};

const defaultBackend: AudioPlayBackend = {
  play(url, volume) {
    (audioContextCtor() !== null ? webAudioBackend : elementBackend).play(
      url,
      volume,
    );
  },
};

/** Start (or restart) the context inside the gesture that called this. */
function unlock(): void {
  const ctx = ensureContext();
  if (ctx === null || ctx.state === "running") return;
  void ctx.resume().catch(() => undefined);
  // iOS starts a context only when a source starts in the gesture too.
  const source = ctx.createBufferSource();
  source.buffer = ctx.createBuffer(1, 1, 22_050);
  source.connect(ctx.destination);
  source.start();
}

let unlockInstalled = false;

/**
 * Start the audio context on the first touch, and again on any touch
 * after the phone suspended it. `urls` are fetched and decoded as soon
 * as the context exists, so the first cue is not also a download.
 * Idempotent.
 */
export function installAudioUnlock(urls: ReadonlyArray<string>): void {
  if (unlockInstalled || typeof window === "undefined") return;
  if (audioContextCtor() === null) return;
  unlockInstalled = true;
  const onGesture = (): void => {
    unlock();
    const ctx = context;
    if (ctx !== null) for (const url of urls) void bufferFor(ctx, url);
  };
  for (const type of ["pointerdown", "touchend", "keydown"] as const) {
    window.addEventListener(type, onGesture, { capture: true, passive: true });
  }
}

let activeBackend: AudioPlayBackend = defaultBackend;

/**
 * Test seam — swap the backend out for a stub. Returns the
 * previous backend so the caller can restore it after the test.
 */
export function setAudioPlayBackend(b: AudioPlayBackend): AudioPlayBackend {
  const prev = activeBackend;
  activeBackend = b;
  return prev;
}

/**
 * Play an audio file at the supplied URL with the supplied options.
 * Mute is honoured before any backend call so a muted user never
 * even constructs the `Audio` object. Volume is clamped via the
 * shared helper so a bogus persisted preference can't blow out the
 * speakers.
 */
export function playAudioFile(url: string, opts: AudioPlayOptions = {}): void {
  if (opts.muted === true) return;
  const volume = clampVolume(opts.volume ?? DEFAULT_AUDIO_VOLUME);
  activeBackend.play(url, volume);
}
